import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, TextField, Typography } from '@mui/material';
import { TransferTargetAudit, TransferTargetKunde } from '../../types';
import { schwachstellenService } from '../../services/schwachstellenService';

interface Props {
  /** Bei true werden die Ziele (neu) geladen und die Auswahl zurueckgesetzt. */
  active: boolean;
  onChange: (audit: TransferTargetAudit | null, kunde: TransferTargetKunde | null) => void;
}

/** Kunde -> Audit Auswahl (wie beim Verschieben von Findings), fuer Import und Freigaben. */
const AuditTargetSelect: React.FC<Props> = ({ active, onChange }) => {
  const [kunden, setKunden] = useState<TransferTargetKunde[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedKunde, setSelectedKunde] = useState<TransferTargetKunde | null>(null);
  const [selectedAudit, setSelectedAudit] = useState<TransferTargetAudit | null>(null);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setSelectedKunde(null);
    setSelectedAudit(null);
    onChangeRef.current(null, null);
    schwachstellenService
      .transferTargets()
      .then((data) => !cancelled && setKunden(data))
      .catch(() => !cancelled && setLoadError('Audits konnten nicht geladen werden.'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [active]);

  const auditOptions = useMemo(() => selectedKunde?.audits ?? [], [selectedKunde]);

  if (loading) return <CircularProgress size={28} />;
  if (loadError) return <Typography color="error">{loadError}</Typography>;
  if (kunden.length === 0) return <Typography color="text.secondary">Keine Audits verfügbar.</Typography>;

  return (
    <>
      <Autocomplete
        options={kunden}
        value={selectedKunde}
        onChange={(_e, next) => {
          setSelectedKunde(next);
          setSelectedAudit(null);
          onChange(null, next);
        }}
        getOptionLabel={(o) => o.name}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        renderInput={(params) => <TextField {...params} label="Kunde" size="small" />}
        sx={{ mb: 2, mt: 0.5 }}
      />
      <Autocomplete
        options={auditOptions}
        value={selectedAudit}
        onChange={(_e, next) => {
          setSelectedAudit(next);
          onChange(next, selectedKunde);
        }}
        getOptionLabel={(o) => (o.projekt_name ? `${o.name} (${o.projekt_name})` : o.name)}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        disabled={!selectedKunde}
        noOptionsText="Keine Audits für diesen Kunden"
        renderInput={(params) => <TextField {...params} label="Audit" size="small" data-testid="audit-target-select" />}
      />
    </>
  );
};

export default AuditTargetSelect;
